import { useCallback, useEffect, useState } from 'react';
import { useQueryClient } from 'react-query';
import { useParams } from 'react-router';
import { i18n, i18nTemplate } from 'app/utils/i18n';
import { createNewDraft } from 'app/utils/draft';
import { useDelayedSave } from 'app/utils/hooks/delayed-save';
import { dispatchMessage, EventType } from 'app/useToasts';
import { draftQueryKey, NO_DRAFT } from 'app/api/draft-queries';
import { registerPendingDesignDraftSave } from './utils/pending-design-saves';
import { useDesignDraft } from './hooks/useDesignDraft';
import { useResetOnDraftDiscard } from './hooks/useResetOnDraftDiscard';
import {
  ClassMap,
  classMapToStyle,
  styleToClassMap,
  resolveLists,
  toolbarCommentFrom,
  scopeCommentFrom,
  extractClassMetadata,
  entryLabelValue,
  ExistingToolbarEntries,
  ExistingScopeEntries,
  ResolvedLists,
} from './utils/class-map';
import { buildGroups, cloneDefinition, reorderEntries, placeCloneAfterSource, ListKey } from './utils/class-lists';
import { ClassListGroup } from './components/ClassListGroup';
import { ClassDefinition } from './components/ClassEditor';
import { ClassStyleModal } from './components/ClassStyleModal';
import { isBuiltinSelector, BuiltinSelectorName, getVisibilityVar } from './utils/builtin-selectors';
import { lintCustomCss, wrapDisplayForVisibilityToggle } from './utils/scss-formatting';
import './FormClassStyles.scss';

function buildSlotBodyFromDef(name: BuiltinSelectorName, def: ClassDefinition): string {
  const props = Object.entries(def.properties)
    .filter(([, v]) => v && v.trim())
    .map(([k, v]) => `${k}: ${v};`)
    .join('\n');
  const custom = def.customCss?.trim() || '';
  const combined = [props, custom].filter(Boolean).join('\n');
  if (!combined) return '';
  const wrapped = wrapDisplayForVisibilityToggle(combined, getVisibilityVar(name));
  const linted = lintCustomCss(wrapped);
  return linted.balanced ? linted.output : combined;
}

const GROUP_LABELS: Record<ListKey, string> = {
  toolbar: 'dc.class.group-toolbar',
  'financial-toolbar': 'dc.class.group-financial',
  page: 'dc.class.group-page',
  table: 'dc.class.group-table',
};

export function FormClassStylesInline() {
  const { documentId } = useParams<{ documentId: string }>();
  const queryClient = useQueryClient();
  const { pDocument, designEntry, style, draft, draftEntry, saveDraft } = useDesignDraft({ styleType: 'form-classes', parsed: true });

  const documentStyle: Record<string, any> = style.data?.content ?? {};
  const documentMetadata = style.data?.metadata ?? designEntry?.metadata;
  const draftStyle: Record<string, any> | undefined = draftEntry?.style;
  const draftMetadata = draftEntry?.metadata;
  const [stateClassMap, setStateClassMap] = useState<ClassMap | null>(null);
  const [stateLists, setStateLists] = useState<ResolvedLists | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [editingClass, setEditingClass] = useState<{ name: string; def: ClassDefinition } | null>(null);
  const [cloneSource, setCloneSource] = useState<string | null>(null);

  useResetOnDraftDiscard(
    draft.data,
    useCallback(() => {
      setStateClassMap(null);
      setStateLists(null);
    }, [])
  );

  const effectiveStyle = draftEntry ? draftStyle ?? {} : documentStyle;
  const effectiveMetadata = draftEntry ? draftMetadata ?? documentMetadata : documentMetadata;
  const classMap: ClassMap = stateClassMap ?? styleToClassMap(effectiveStyle, effectiveMetadata);

  const existingToolbarEntries: ExistingToolbarEntries = {
    ...style.data?.toolbarEntries,
    ...draftEntry?.toolbarEntries,
  };

  const existingScopeEntries: ExistingScopeEntries = {
    ...style.data?.scopeEntries,
    ...draftEntry?.scopeEntries,
  };

  const resolved: ResolvedLists = resolveLists(
    classMap,
    stateLists ? { toolbar: stateLists.toolbar, financialToolbar: stateLists['financial-toolbar'] } : existingToolbarEntries,
    stateLists ? { pageClasses: stateLists.page, tableClasses: stateLists.table } : existingScopeEntries
  );

  const [pushDraft, cancelPushDraft] = useDelayedSave(({ classMap: newClassMap, lists }: { classMap: ClassMap; lists: ResolvedLists }) => {
    const { style: cssStyle, rawScss } = classMapToStyle(newClassMap);
    const toolbarComment = toolbarCommentFrom(lists);
    const scopeComment = scopeCommentFrom(lists);
    const metadata = extractClassMetadata(newClassMap);

    const entry: any = {
      type: 'form-classes' as const,
      style: cssStyle,
      metadata,
    };
    if (rawScss) {
      entry.rawScss = rawScss;
    }
    if (toolbarComment) {
      entry.toolbarComment = toolbarComment;
    }
    if (scopeComment) {
      entry.scopeComment = scopeComment;
    }
    const latestDraft: any = queryClient.getQueryData(draftQueryKey(documentId)) ?? NO_DRAFT;
    const newDraft = createNewDraft(pDocument, latestDraft, entry);
    const preSaveHadFormClasses =
      latestDraft !== NO_DRAFT && Array.isArray(latestDraft?.content) && latestDraft.content.some((d: any) => d.type === 'form-classes');
    const hasBuiltinInDraft = newDraft.content?.some((d: any) => d.type === 'builtin-css');
    if (!hasBuiltinInDraft && !preSaveHadFormClasses) {
      const publishedSlots = (style.data as any)?.builtinSlots as Record<string, string> | undefined;
      if (publishedSlots && Object.values(publishedSlots).some((v) => typeof v === 'string' && v.trim())) {
        newDraft.content.push({ type: 'builtin-css', slots: publishedSlots });
      }
    }

    saveDraft(newDraft);
  }, 1000);
  useEffect(() => registerPendingDesignDraftSave(cancelPushDraft), [cancelPushDraft]);

  const commit = (updated: ClassMap, lists: ResolvedLists) => {
    setStateClassMap(updated);
    setStateLists(lists);
    pushDraft({ classMap: updated, lists });
  };

  const handleSaveClass = (className: string, def: ClassDefinition, oldClassName?: string) => {
    const updated = { ...classMap };
    if (oldClassName && oldClassName !== className) {
      delete updated[oldClassName];
    }
    updated[className] = def;

    let lists = resolveLists(
      updated,
      { toolbar: resolved.toolbar, financialToolbar: resolved['financial-toolbar'] },
      { pageClasses: resolved.page, tableClasses: resolved.table }
    );
    if (cloneSource) {
      lists = placeCloneAfterSource(lists, className, cloneSource);
    }

    commit(updated, lists);
  };

  const handleRemoveClass = (className: string) => {
    const updated = { ...classMap };
    delete updated[className];
    commit(
      updated,
      resolveLists(
        updated,
        { toolbar: resolved.toolbar, financialToolbar: resolved['financial-toolbar'] },
        { pageClasses: resolved.page, tableClasses: resolved.table }
      )
    );
  };

  const handleReorder = (list: ListKey, order: string[]) => {
    commit(classMap, { ...resolved, [list]: reorderEntries(resolved[list], order) });
  };

  const handleRelabelEntry = (list: ListKey, key: string, labelSv: string, labelEn: string) => {
    // Same guard the class labels use. A blur that changes nothing must not write: a legacy
    // unprefixed label would otherwise be rewritten on a mere tab-through.
    const next = entryLabelValue(labelSv, labelEn);
    if (next === resolved[list][key]) {
      return;
    }
    commit(classMap, { ...resolved, [list]: { ...resolved[list], [key]: next } });
  };

  const handleClone = (className: string) => {
    const clone = cloneDefinition(className, classMap[className], Object.keys(classMap));
    setCloneSource(className);
    setEditingClass({ name: clone.name, def: clone.def });
    setShowModal(true);
  };

  const handleMigrateToSlot = (className: string) => {
    if (!isBuiltinSelector(className)) return;
    const def = classMap[className];
    const slotBody = buildSlotBodyFromDef(className, def);

    const updatedClassMap = { ...classMap };
    delete updatedClassMap[className];
    const updatedLists = resolveLists(
      updatedClassMap,
      { toolbar: resolved.toolbar, financialToolbar: resolved['financial-toolbar'] },
      { pageClasses: resolved.page, tableClasses: resolved.table }
    );
    const { style: cssStyle, rawScss } = classMapToStyle(updatedClassMap);
    const toolbarComment = toolbarCommentFrom(updatedLists);
    const scopeComment = scopeCommentFrom(updatedLists);
    const metadata = extractClassMetadata(updatedClassMap);
    const formClassesEntry: any = { type: 'form-classes' as const, style: cssStyle, metadata };
    if (rawScss) formClassesEntry.rawScss = rawScss;
    if (toolbarComment) formClassesEntry.toolbarComment = toolbarComment;
    if (scopeComment) formClassesEntry.scopeComment = scopeComment;

    const latestDraft: any = queryClient.getQueryData(draftQueryKey(documentId)) ?? NO_DRAFT;
    const existingBuiltin =
      latestDraft && latestDraft !== NO_DRAFT
        ? (latestDraft.content?.find((d: any) => d.type === 'builtin-css') as { slots?: Record<string, string> } | undefined)
        : undefined;
    const builtinEntry: any = {
      type: 'builtin-css' as const,
      slots: { ...(existingBuiltin?.slots ?? {}), [className]: slotBody },
    };

    let newDraft = createNewDraft(pDocument, latestDraft, formClassesEntry);
    newDraft = createNewDraft(pDocument, newDraft, builtinEntry);

    setStateClassMap(updatedClassMap);
    setStateLists(updatedLists);
    saveDraft(newDraft, {
      onSuccess: () => {
        dispatchMessage(EventType.ADD_MESSAGE, {
          id: `migrate-builtin-${className}-${Date.now()}`,
          type: 'success',
          title: i18n('dc.builtin.migrated-title'),
          text: i18nTemplate('dc.builtin.migrated-text', { className: `.${className}` }),
          removeTime: 4000,
        });
      },
    });
  };

  const handleOpenAdd = () => {
    setCloneSource(null);
    setEditingClass(null);
    setShowModal(true);
  };

  const handleOpenEdit = (className: string) => {
    setCloneSource(null);
    setEditingClass({ name: className, def: classMap[className] });
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingClass(null);
    setCloneSource(null);
  };

  const { groups, ungrouped } = buildGroups(classMap, resolved);
  const isEmpty = groups.every((group) => group.rows.length === 0) && ungrouped.length === 0;

  const rowHandlers = {
    classMap,
    onReorder: handleReorder,
    onRelabel: handleRelabelEntry,
    onEdit: handleOpenEdit,
    onClone: handleClone,
    onRemove: handleRemoveClass,
    onMigrate: handleMigrateToSlot,
  };

  return (
    <div className="form-class-styles">
      <p className="text-muted fs-7 mb-3">{i18n('dc.class.description')}</p>
      {isEmpty && <p className="text-muted fs-7 fst-italic">{i18n('dc.class.no-styles')}</p>}
      {!isEmpty && (
        <table className="table table-sm table-hover mb-3 form-class-styles__table">
          <colgroup>
            <col style={{ width: 28 }} />
            <col style={{ width: '22%' }} />
            <col style={{ width: '22%' }} />
            <col style={{ width: '22%' }} />
            <col />
            <col style={{ width: 120 }} />
          </colgroup>
          <thead>
            <tr>
              <th />
              <th className="fs-7">{i18n('dc.class.col-selector')}</th>
              <th className="fs-7">{i18n('dc.class.col-name-sv')}</th>
              <th className="fs-7">{i18n('dc.class.col-name-en')}</th>
              <th className="fs-7">{i18n('dc.class.col-properties')}</th>
              <th />
            </tr>
          </thead>
          {groups.map((group) => (
            <ClassListGroup key={group.list} list={group.list} rows={group.rows} titleKey={GROUP_LABELS[group.list]} {...rowHandlers} />
          ))}
          <ClassListGroup
            list={null}
            rows={ungrouped.map((name) => ({ key: name, label: '', className: name, kind: 'class' as const }))}
            titleKey="dc.class.group-ungrouped"
            hintKey="dc.class.group-ungrouped-hint"
            {...rowHandlers}
          />
        </table>
      )}
      <button className="btn btn-sm btn-outline-primary" onClick={handleOpenAdd}>
        <i className="fa-regular fa-plus me-1" />
        {i18n('dc.class.add-style')}
      </button>
      {showModal && (
        <ClassStyleModal
          show={showModal}
          onHide={handleCloseModal}
          onSave={handleSaveClass}
          existingClassNames={Object.keys(classMap)}
          editClassName={cloneSource ? null : editingClass?.name}
          editDef={editingClass?.def}
          prefillName={cloneSource ? editingClass?.name : undefined}
          classMap={classMap}
        />
      )}
    </div>
  );
}
